import { Button, ErrorMessage, Skeleton, Tooltip } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { cssVar } from '@docnboard/theme';
import clsx from 'clsx';
import { useMemo } from 'react';

import { UserPlanButton } from '../../auth/user-plan-button';
import * as styles from './usage-progress.css';

export interface UsageProgressProps {
  upgradable?: boolean;
  onUpgrade?: () => void;
  hasPayment?: boolean;
  limit?: string;
  loading?: boolean;
  error?: any;
  isFreeUser?: boolean;
  percent?: number;
  color?: string;
  title?: string;
  classNameRoot?: string;
}

enum ButtonType {
  Primary = 'primary',
  Default = 'secondary',
}

export const UsageProgress = ({
  onUpgrade,
  loading,
  hasPayment,
  error,
  isFreeUser,
  limit,
  title,
  color,
  percent,
  classNameRoot,
}: UsageProgressProps) => {
  const t = useI18n();

  const buttonType = useMemo(() => {
    if (isFreeUser) {
      return ButtonType.Primary;
    }
    return ButtonType.Default;
  }, [isFreeUser]);

  if (loading) {
    if (error) {
      return (
        <ErrorMessage>{t['docnboard.user-info.usage.error']()}</ErrorMessage>
      );
    }
    return <Skeleton height={42} />;
  }

  return (
    <div className={clsx(styles.usageProgressContainer, classNameRoot)}>
      <div className={styles.usageProgressWrapper}>
        <div className="usage-progress-desc">
          <span>{title}</span>
          <div className="flex items-center gap-2">
            <span>{limit}</span>
            <UserPlanButton />
          </div>
        </div>

        <div className="usage-progress-bar-wrapper">
          <div
            className={styles.usageProgressBar}
            style={{
              width: `${percent}%`,
              backgroundColor: color ?? cssVar('processingColor'),
            }}
          ></div>
        </div>
      </div>

      {hasPayment ? (
        <Tooltip
          options={{ hidden: (percent ?? 0) < 100 }}
          content={
            isFreeUser
              ? t['com.affine.storage.maximum-tips']()
              : t['com.affine.storage.maximum-tips.pro']()
          }
        >
          <span tabIndex={0}>
            <Button variant={buttonType} onClick={onUpgrade}>
              {isFreeUser
                ? t['com.affine.storage.upgrade']()
                : t['com.affine.storage.change-plan']()}
            </Button>
          </span>
        </Tooltip>
      ) : null}
    </div>
  );
};
